"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { axiosGateway } from "../../api/axios";
import { MESSAGES_PATHS } from "../../api/endpoints";
import { ensureUploadUrl, looksImageLike } from "../../utils/uploads";
import { useSocket } from "../context/SocketContext";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";

const PAGE_SIZE = 30;

function normalizeMessage(m) {
  if (!m) return null;
  const src = m?.message && typeof m.message === "object" ? m.message : m;

  let fileUrl = src.fileUrl || undefined;
  if (fileUrl && !/^(blob:|data:|https?:\/\/)/i.test(fileUrl)) {
    fileUrl = ensureUploadUrl(fileUrl);
  }

  const finalType =
    fileUrl && looksImageLike({ fileUrl, fileName: src.fileName, mimeType: src.mimeType })
      ? "image"
      : (src.messageType || src.type || "text").toLowerCase();

  return {
    id: src._id || src.id || `tmp_${Math.random().toString(36).slice(2)}`,
    fromUserId: src.fromUserId || src.userA || src.sender || src.from,
    toUserId: src.toUserId || src.userB || src.recipient || src.to,
    content: src.content || src.text || "",
    messageType: finalType,
    createdAt: src.createdAt || new Date().toISOString(),
    fileUrl,
    fileName: src.fileName,
    mimeType: src.mimeType,
    fileSize: src.fileSize,
    isRead: src.isRead === undefined ? undefined : Boolean(src.isRead),
  };
}

function uniqById(list) {
  const map = new Map();
  for (const x of list) map.set(x.id, x);
  return [...map.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}

function stripDoctorPrefix(id) {
  if (!id) return id;
  return String(id).replace(/^DR_/, "");
}

// `conversationId` switches the hook to the DOCTOR_DOCTOR conversation API.
export default function useMessaging(peerId, { autoFetch = true, meOverride, conversationId = null } = {}) {
  const { connected: socketConnected, emit, subscribe } = useSocket();
  const effectiveUserId = useSelector(selectEffectiveUserId);

  const authId = effectiveUserId || null;
  const me = useMemo(() => (meOverride ? String(meOverride) : authId ? String(authId) : null), [meOverride, authId]);
  const meStripped = useMemo(() => stripDoctorPrefix(me), [me]);
  const peerStripped = useMemo(() => stripDoctorPrefix(peerId), [peerId]);

  const [messages, setMessages] = useState([]);
  const [typingFrom, setTypingFrom] = useState(null);
  const [loading, setLoading] = useState("idle");
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const oldestRef = useRef(null);
  const typingTimerRef = useRef(null);

  useEffect(() => {
    setTypingFrom(null);
    clearTimeout(typingTimerRef.current);
  }, [peerStripped]);

  // =========================
  // Fetch history
  // =========================
  const fetchHistory = useCallback(async () => {
    if (!autoFetch || !meStripped || !peerStripped) return;

    setLoading("pending");
    setError(null);

    try {
      const url = conversationId
        ? `${MESSAGES_PATHS.CONV_MESSAGES(conversationId)}?limit=${PAGE_SIZE}`
        : `${MESSAGES_PATHS.LIST(meStripped, peerStripped)}&limit=${PAGE_SIZE}`;
      const res = await axiosGateway.get(url, { withCredentials: true });
      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const norm = raw.map(normalizeMessage).filter(Boolean);

      const uniq = uniqById(norm);
      setMessages(uniq);
      setLoading("done");

      oldestRef.current = uniq[0]?.createdAt || null;
      setHasMore(!conversationId && uniq.length >= PAGE_SIZE);
    } catch (err) {
      setLoading("error");
      setError(err?.response?.data?.message || err.message || "Error loading messages");
    }
  }, [autoFetch, meStripped, peerStripped, conversationId]);

  useEffect(() => {
    if (meStripped && peerStripped && autoFetch) fetchHistory();
    else {
      setMessages([]);
      setHasMore(false);
      oldestRef.current = null;
    }
  }, [meStripped, peerStripped, autoFetch, fetchHistory]);

  // =========================
  // Load older messages (pagination)
  // =========================
  const loadOlder = useCallback(async () => {
    if (conversationId || !meStripped || !peerStripped || loadingMore || !hasMore || !oldestRef.current) return { added: 0 };

    setLoadingMore(true);

    try {
      const url = `${MESSAGES_PATHS.LIST(meStripped, peerStripped)}&before=${encodeURIComponent(oldestRef.current)}&limit=${PAGE_SIZE}`;
      const { data } = await axiosGateway.get(url, { withCredentials: true });
      const raw = Array.isArray(data) ? data : data?.data || [];

      if (!raw.length) {
        setHasMore(false);
        setLoadingMore(false);
        return { added: 0 };
      }

      const norm = raw.map(normalizeMessage);
      setMessages((prev) => uniqById([...norm, ...prev]));
      oldestRef.current = norm[0]?.createdAt || oldestRef.current;
      setHasMore(norm.length >= PAGE_SIZE);
      setLoadingMore(false);
      return { added: norm.length };
    } catch (err) {
      setLoadingMore(false);
      setHasMore(false);
      return { added: 0 };
    }
  }, [meStripped, peerStripped, loadingMore, hasMore, conversationId]);

  // =========================
  // Socket subscriptions
  // =========================
  useEffect(() => {
    if (!subscribe || !meStripped || !peerStripped) return;
const handleMessage = (rawMsg) => {
  const normalized = normalizeMessage(rawMsg.message || rawMsg);
  if (!normalized) return;

  const fromStrip = stripDoctorPrefix(normalized.fromUserId);
  const toStrip = stripDoctorPrefix(normalized.toUserId);
  const isInConversation = conversationId
    ? String(rawMsg.conversationId || rawMsg.message?.conversationId || "") === String(conversationId)
    : (fromStrip === meStripped && toStrip === peerStripped) ||
      (fromStrip === peerStripped && toStrip === meStripped);

  if (!isInConversation) return;

  // ✅ Replace optimistic draft using ackOf (clientMsgId)
  const ackId = rawMsg.ackOf || null;

  setMessages((prev) => {
    const withoutOptimistic = ackId
      ? prev.filter((m) => m.id !== ackId)
      : prev;

    const exists = withoutOptimistic.some((m) => m.id === normalized.id);
    if (exists) return withoutOptimistic;
    return uniqById([...withoutOptimistic, normalized]);
  });
};
    const handleTyping = ({ from, isTyping }) => {
      const fromStrip = stripDoctorPrefix(from);
      if (fromStrip === peerStripped) {
        clearTimeout(typingTimerRef.current);
        if (isTyping) {
          setTypingFrom(fromStrip);
          typingTimerRef.current = setTimeout(() => setTypingFrom(null), 3000);
          return;
        }
        setTypingFrom(null);
      }
    };

    const unsubMsg = subscribe(conversationId ? "newMessage" : "message:new", handleMessage);
    const unsubTyping = subscribe("message:typing", handleTyping);
    const unsubRead = conversationId
      ? subscribe("readMessage", (evt) => {
          if (String(evt?.conversationId) !== String(conversationId)) return;
          setMessages((prev) => prev.map((m) => (m.fromUserId && stripDoctorPrefix(m.fromUserId) === meStripped ? { ...m, isRead: true } : m)));
        })
      : () => {};

    return () => {
      unsubMsg();
      unsubTyping();
      unsubRead();
      clearTimeout(typingTimerRef.current);
    };
  }, [subscribe, meStripped, peerStripped, conversationId]);

  // Mark the conversation as read when opened and whenever a peer message arrives.
  const lastIncomingId = useMemo(() => {
    const last = [...messages].reverse().find((m) => stripDoctorPrefix(m.fromUserId) !== meStripped);
    return last?.id || null;
  }, [messages, meStripped]);

  useEffect(() => {
    if (!conversationId || !lastIncomingId || !emit || !socketConnected) return;
    emit("readMessage", { conversationId, peerId: peerStripped });
  }, [conversationId, lastIncomingId, emit, socketConnected, peerStripped]);


const pendingRef = useRef(new Map()); // clientMsgId -> { draft, resolve, reject }

const sendText = useCallback(
  (text) => {
    const content = text?.trim();
    if (!content || !meStripped || !peerStripped) return;

    const clientMsgId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // ✅ Optimistic message shown immediately
    const draft = normalizeMessage({
      id: clientMsgId,
      fromUserId: meStripped,
      toUserId: peerStripped,
      content,
      messageType: "text",
      createdAt: new Date().toISOString(),
    });

    setMessages((prev) => uniqById([...prev, draft]));

    if (conversationId) {
      if (!emit || !socketConnected) {
        return axiosGateway
          .post(MESSAGES_PATHS.CONV_MESSAGES(conversationId), { content, messageType: "text" }, { withCredentials: true })
          .then((res) => {
            const saved = normalizeMessage(res.data?.message || res.data);
            if (saved) setMessages((prev) => uniqById([...prev.filter((m) => m.id !== clientMsgId), saved]));
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event("doctor-conversations:refresh"));
            }
          })
          .catch(() => setMessages((prev) => prev.filter((m) => m.id !== clientMsgId)));
      }
      emit("sendMessage", { conversationId, content, messageType: "text", clientMsgId });
      return;
    }

    if (!emit || !socketConnected) {
      // ✅ Fallback to HTTP if socket not ready
      return axiosGateway
        .post(MESSAGES_PATHS.SEND, { userA: meStripped, userB: peerStripped, content, messageType: "text" }, { withCredentials: true })
        .then((res) => {
          const saved = normalizeMessage(res.data?.message || res.data);
          if (saved) setMessages((prev) => uniqById([...prev.filter((m) => m.id !== clientMsgId), saved]));
        })
        .catch(() => setMessages((prev) => prev.filter((m) => m.id !== clientMsgId)));
    }

    // ✅ Pure WS path — gateway saves + echoes back via message:new
    emit("message:new", {
      userA: meStripped,
      userB: peerStripped,
      content,
      messageType: "text",
      clientMsgId,  // gateway returns this in ackOf
    });

    // The socket subscription below replaces the optimistic with the real saved message
  },
  [meStripped, peerStripped, emit, socketConnected, conversationId]
);
  // =========================
  // Upload file
  // =========================
  const uploadFile = useCallback(
    async (file, caption) => {
      if (!file || !meStripped || !peerStripped) return;
      if (conversationId) throw new Error("File upload is not supported in doctor conversations");

      const tempId = `tmp_file_${Date.now()}`;
      let optimistic = null;

      if (looksImageLike({ fileName: file.name, type: file.type })) {
        const tmpUrl = URL.createObjectURL(file);
        optimistic = normalizeMessage({
          id: tempId,
          fromUserId: meStripped,
          toUserId: peerStripped,
          content: caption || "",
          messageType: "image",
          fileUrl: tmpUrl,
          fileName: file.name,
          mimeType: file.type,
          createdAt: new Date().toISOString(),
        });
        setMessages((prev) => uniqById([...prev, optimistic]));
      }

      const form = new FormData();
      form.append("userA", meStripped);
      form.append("userB", peerStripped);
      if (caption) form.append("content", caption);
      form.append("file", file);

      try {
        const res = await axiosGateway.post(MESSAGES_PATHS.UPLOAD, form, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        const saved = normalizeMessage(res.data?.message || res.data);
        if (saved) {
          setMessages((prev) => uniqById([...prev.filter((m) => m.id !== tempId), saved]));
          if (emit && socketConnected) emit("message:new", { message: saved });
        }

        if (optimistic) URL.revokeObjectURL(optimistic.fileUrl);
        return saved;
      } catch (err) {
        if (optimistic) setMessages((prev) => prev.filter((m) => m.id !== tempId));
        if (optimistic) URL.revokeObjectURL(optimistic.fileUrl);
        throw err;
      }
    },
    [meStripped, peerStripped, emit, socketConnected, conversationId]
  );

  // =========================
  // Emit typing
  // =========================
  const emitTyping = useCallback((isTyping = true) => {
    if (emit && peerStripped && socketConnected) {
      emit("message:typing", { to: peerStripped, isTyping: Boolean(isTyping) });
    }
  }, [peerStripped, emit, socketConnected]);

  return useMemo(
    () => ({
      me: meStripped,
      messages,
      loading,
      error,
      typingFrom,
      socketConnected,
      sendText,
      uploadFile,
      emitTyping,
      hasMore,
      loadingMore,
      loadOlder,
    }),
    [meStripped, messages, loading, error, typingFrom, socketConnected, sendText, uploadFile, emitTyping, hasMore, loadingMore, loadOlder]
  );
}
